<template>
  <input ref="inputRef" @blur="onBlur" :data-cy="cypressLocator" />
</template>

<script>
import { ref, watch, onMounted, nextTick } from 'vue';
import { isEqual } from 'lodash';
import { useCurrencyInput } from 'vue-currency-input';
import { FormCypressLocatorMixin } from '@/modules/core/form/mixins/FormCypressLocatorMixin';
import { EVENT } from '@/modules/core/form/form.constants';

export default {
  name: 'CurrencyInput',
  mixins: [FormCypressLocatorMixin],
  props: {
    value: { type: [Number, String], default: undefined },
    options: {
      type: Object,
      default: () => ({}),
    },
    cy: {
      type: String,
      required: false,
      default: null,
    },
  },
  setup(props, { emit }) {
    const isInitialized = ref(false);
    const isSettingValue = ref(false);
    const isResettingEmptyValue = ref(false);
    const { inputRef, setOptions, setValue, numberValue } = useCurrencyInput(props?.options, false);

    watch(numberValue, (value) => {
      if (isSettingValue.value || isResettingEmptyValue.value) {
        return;
      }
      if (!isInitialized.value) {
        isInitialized.value = true;
        return;
      }
      emit(EVENT.INPUT, value);
    });

    // The API sends '' for a currency with no value; the library would format it as 0, so it must reach it as null.
    const updateValue = (value) => {
      isSettingValue.value = true;
      setValue(value === '' ? null : value);
      Promise.resolve().then(() => {
        isSettingValue.value = false;
      });
    };

    const updateOptions = (options) => {
      setOptions(options);
    };

    onMounted(() => {
      isInitialized.value = true;
      if (props.value === '') {
        // The library initialises itself from the raw '' prop after mount, as 0: reset it once that is done,
        // and do not report that 0 in between.
        isResettingEmptyValue.value = true;
        nextTick(() => {
          updateValue(null);
          isResettingEmptyValue.value = false;
        });
      }
    });

    return { inputRef, updateValue, updateOptions, numberValue };
  },
  computed: {
    defaultCYLocator() {
      return 'currency-element';
    },
  },
  watch: {
    /**
     * When v-model is changed:
     *   1. Set internal value.
     */
    value(newValue) {
      this.updateValue(newValue);
    },
    options(newOptions, oldOptions) {
      // setOptions re-formats the input to full precision, which breaks typing mid-value
      if (isEqual(newOptions, oldOptions)) {
        return;
      }
      this.updateOptions(newOptions);
    },
  },
  methods: {
    onBlur($event) {
      this.$emit('blur', $event);
      this.$emit('change', this.numberValue);
    },
  },
};
</script>
